import { KeyboardEvent, ReactNode, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { SelectButton } from 'primereact/selectbutton';
import { ScheduleHoursDTO } from '../../dto/ServiceStatisticsDTO';
import {
    buildJornadaRows, buildWeekdayRows, hourMarks, scheduleDomain, ScheduleRow, ScheduleSummary, shortDay,
    summarizeSchedule,
} from './scheduleHours';
import { modeMessageId, StatisticsMode } from './statisticsMode';
import { formatInt } from './statisticsUtils';
import { weekdayNames } from './chartText';

type ScheduleView = 'weekday' | 'jornada';

interface ScheduleHoursChartProps {
    data: ScheduleHoursDTO;
    mode?: StatisticsMode;
}

/** Margen del eje a cada lado, para que el rótulo de la primera y la última hora no se corte. */
const AXIS_PAD = 16;
const AXIS_HEIGHT = 28;
/** Ancho mínimo entre dos rótulos del eje, para que no se pisen. */
const LABEL_SPACING = 48;
/** Filas por día de la semana: 44 px; por jornada, compactas de 36 px (hay hasta 120). */
const ROW_HEIGHT = { weekday: 44, jornada: 36 } as const;
const BAR_HEIGHT = { weekday: 10, jornada: 8 } as const;
const BAR_TOP = { weekday: [9, 25], jornada: [8, 20] } as const;
const MIN_BAR_WIDTH = 2;

/**
 * Horario programado contra el real de una agenda de consultorio. Una fila por día de la semana (o por bloque, o por
 * jornada): una barra gris con la apertura y el cierre, y debajo la barra de lo real, desde el primer paciente atendido
 * hasta la última atención terminada. Lo que pasa del cierre va rayado y en otro color, con «+N min» al final de la fila,
 * y una línea punteada marca el cierre. Cada fila se alcanza con el teclado y dice lo mismo en texto que en el gráfico.
 * TODO(design-system): gráficos. Novarium no define este gráfico; se arma solo con tokens (--divider y --text-2 para lo
 * programado, --primary para lo real, --warn para lo que pasa del cierre).
 */
export default function ScheduleHoursChart({ data, mode = 'management' }: ScheduleHoursChartProps) {
    const intl = useIntl();
    const patternId = useId();
    const [view, setView] = useState<ScheduleView>('weekday');
    const [activeKey, setActiveKey] = useState<string | null>(null);
    const trackRef = useRef<HTMLDivElement>(null);
    const [trackWidth, setTrackWidth] = useState(0);

    const rows = useMemo(() => (view === 'weekday' ? buildWeekdayRows(data) : buildJornadaRows(data)), [data, view]);
    const summary = useMemo(() => summarizeSchedule(data), [data]);
    const days = useMemo(() => weekdayNames(intl), [intl]);
    const dayShort = (dayOfWeek: number) => intl.formatMessage({ id: `ServicesStatisticsScheduleWeekdayShort${dayOfWeek}` });

    const clockFormat = useMemo(
        () => new Intl.DateTimeFormat(intl.locale, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }), [intl.locale]);
    const clock = (minute: number) => clockFormat.format(new Date(2000, 0, 1, 0, minute));

    // El ancho de la pista sale del eje: las filas usan las mismas columnas
    useLayoutEffect(() => {
        const element = trackRef.current;
        if (!element) return undefined;
        setTrackWidth(element.clientWidth);
        const observer = new ResizeObserver(entries => setTrackWidth(Math.round(entries[0].contentRect.width)));
        observer.observe(element);
        return () => observer.disconnect();
    }, [rows.length > 0]);

    useEffect(() => { setActiveKey(null); }, [data, view]);

    const domain = useMemo(() => (rows.length > 0 ? scheduleDomain(rows) : null), [rows]);
    const marks = useMemo(() => (domain ? hourMarks(domain) : []), [domain]);

    const plotWidth = Math.max(0, trackWidth - 2 * AXIS_PAD);
    const x = (minute: number): number => {
        if (!domain) return 0;
        return AXIS_PAD + ((minute - domain.start) / (domain.end - domain.start)) * plotWidth;
    };
    const pxPerHour = domain ? plotWidth / ((domain.end - domain.start) / 60) : 0;
    const labelEvery = Math.max(1, Math.ceil(LABEL_SPACING / Math.max(pxPerHour, 1)));

    // ── Textos ──

    const summaryText = (value: ScheduleSummary): ReactNode => {
        const bold = (chunks: ReactNode) => <strong>{chunks}</strong>;
        if (value.kind === 'onTime') return intl.formatMessage({ id: modeMessageId(mode, 'ScheduleSummaryOnTime') });
        if (value.kind === 'lateMixed') {
            return intl.formatMessage({ id: modeMessageId(mode, 'ScheduleSummaryLateMixed') },
                { minutes: formatInt(value.minutes), days: formatInt(value.days), total: value.total, b: bold });
        }
        return intl.formatMessage({ id: modeMessageId(mode, 'ScheduleSummaryLate') }, {
            close: clock(value.closeMinute), last: clock(value.lastMinute), minutes: formatInt(value.minutes),
            days: formatInt(value.days), total: value.total, b: bold,
        });
    };

    const rowDay = (row: ScheduleRow): string => (row.blockLabel
        ? `${days[row.dayOfWeek - 1]} (${row.blockLabel})` : days[row.dayOfWeek - 1]);

    /** El mismo texto para el rótulo accesible de la fila y para el detalle que se ve al apuntar o enfocar. */
    const rowText = (row: ScheduleRow): string => {
        const common = {
            from: row.scheduledFromText, to: row.scheduledToText, first: row.firstServedText, last: row.lastCompletedText,
            minutes: formatInt(row.overrunMinutes),
        };
        if (row.date === null) {
            const values = { ...common, day: rowDay(row), days: formatInt(row.overrunDays), total: row.days, onTime: row.days - row.overrunDays };
            return intl.formatMessage({ id: modeMessageId(mode, row.late ? 'ScheduleDetailWeekdayLate' : 'ScheduleDetailWeekdayOnTime') }, values);
        }
        const date = `${days[row.dayOfWeek - 1]} ${new Date(`${row.date}T00:00:00`).toLocaleDateString(intl.locale)}`;
        return intl.formatMessage({ id: modeMessageId(mode, row.late ? 'ScheduleDetailJornadaLate' : 'ScheduleDetailJornadaOnTime') },
            { ...common, date, overbooked: String(row.overbooked) });
    };

    const active = rows.find(row => row.key === activeKey) ?? null;

    const viewOptions = [
        { value: 'weekday' as const, label: intl.formatMessage({ id: 'ServicesStatisticsScheduleViewWeekday' }) },
        { value: 'jornada' as const, label: intl.formatMessage({ id: 'ServicesStatisticsScheduleViewJornada' }) },
    ];

    /** Flechas, Inicio y Fin pasan de una fila a otra (con 120 jornadas, Tab recorre una sola vez la lista). */
    const moveFocus = (event: KeyboardEvent<HTMLDivElement>) => {
        const row = event.currentTarget;
        const siblings = Array.from(row.parentElement?.querySelectorAll<HTMLElement>('.ss-sched__row') ?? []);
        const index = siblings.indexOf(row);
        const target = event.key === 'ArrowDown' ? siblings[index + 1]
            : event.key === 'ArrowUp' ? siblings[index - 1]
                : event.key === 'Home' ? siblings[0]
                    : event.key === 'End' ? siblings[siblings.length - 1] : undefined;
        if (target) {
            event.preventDefault();
            target.focus();
        }
    };

    const compact = view === 'jornada';
    const rowHeight = compact ? ROW_HEIGHT.jornada : ROW_HEIGHT.weekday;
    const barHeight = compact ? BAR_HEIGHT.jornada : BAR_HEIGHT.weekday;
    const [scheduledTop, realTop] = compact ? BAR_TOP.jornada : BAR_TOP.weekday;

    const renderBars = (row: ScheduleRow) => {
        const scheduledX = x(row.scheduledFrom);
        const scheduledWidth = Math.max(MIN_BAR_WIDTH, x(row.scheduledTo) - scheduledX);
        const firstX = x(row.firstServed);
        const closeX = x(row.scheduledTo);
        const lastX = Math.max(x(row.lastCompleted), firstX + MIN_BAR_WIDTH);
        // Lo real hasta el cierre; lo que pasa del cierre, aparte (solo si la fila terminó tarde)
        const onTimeEnd = row.late ? Math.min(lastX, Math.max(closeX, firstX)) : lastX;
        const overStart = Math.max(closeX, firstX);
        return (
            <>
                <rect className="ss-sched__scheduled" x={scheduledX} y={scheduledTop} width={scheduledWidth} height={barHeight} rx={2} />
                {onTimeEnd - firstX > 0 && (
                    <rect className="ss-sched__real" x={firstX} y={realTop} width={Math.max(MIN_BAR_WIDTH, onTimeEnd - firstX)}
                        height={barHeight} rx={2} />
                )}
                {row.late && lastX - overStart > 0 && (
                    <rect className="ss-sched__over" x={overStart} y={realTop} width={Math.max(MIN_BAR_WIDTH, lastX - overStart)}
                        height={barHeight} rx={2} fill={`url(#${patternId})`} />
                )}
                <line className="ss-sched__close" x1={closeX} x2={closeX} y1={0} y2={rowHeight} />
            </>
        );
    };

    return (
        <div className="ss-sched">
            <p className="ss-summary" data-testid="schedule-summary">{summaryText(summary)}</p>

            <div className="ss-measure">
                <span id="ss-sched-view" className="nv-sr-only">{intl.formatMessage({ id: 'ServicesStatisticsScheduleViewBy' })}</span>
                <SelectButton value={view} options={viewOptions} optionLabel="label" optionValue="value"
                    onChange={event => { if (event.value) setView(event.value); }} aria-labelledby="ss-sched-view" />
            </div>

            <svg className="ss-sched__defs" width="0" height="0" aria-hidden="true" focusable="false">
                <defs>
                    <pattern id={patternId} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
                        <rect className="ss-sched__pattern-bg" width="6" height="6" />
                        <line className="ss-sched__pattern-line" x1="0" y1="0" x2="0" y2="6" strokeWidth="2" />
                    </pattern>
                </defs>
            </svg>

            <ul className="ss-sched__legend" aria-label={intl.formatMessage({ id: 'ServicesStatisticsScheduleLegend' })}>
                <li>
                    <svg width="24" height="12" aria-hidden="true">
                        <rect className="ss-sched__scheduled" x="1" y="2" width="22" height="8" rx="2" />
                    </svg>
                    {intl.formatMessage({ id: 'ServicesStatisticsScheduleLegendScheduled' })}
                </li>
                <li>
                    <svg width="24" height="12" aria-hidden="true">
                        <rect className="ss-sched__real" x="1" y="2" width="22" height="8" rx="2" />
                    </svg>
                    {intl.formatMessage({ id: 'ServicesStatisticsScheduleLegendReal' })}
                </li>
                <li>
                    <svg width="24" height="12" aria-hidden="true">
                        <rect className="ss-sched__over" x="1" y="2" width="22" height="8" rx="2" fill={`url(#${patternId})`} />
                    </svg>
                    {intl.formatMessage({ id: 'ServicesStatisticsScheduleLegendOver' })}
                </li>
                <li>
                    <svg width="24" height="12" aria-hidden="true">
                        <line className="ss-sched__close" x1="12" x2="12" y1="0" y2="12" />
                    </svg>
                    {intl.formatMessage({ id: 'ServicesStatisticsScheduleLegendClose' })}
                </li>
            </ul>

            {!domain ? <p className="nv-muted">{intl.formatMessage({ id: 'ServicesStatisticsScheduleNoJornadas' })}</p> : (
            <div className={compact ? 'ss-sched__scroll ss-sched__scroll--jornada' : 'ss-sched__scroll'}>
                <div className="ss-sched__axis" aria-hidden="true">
                    <span />
                    <div ref={trackRef} className="ss-sched__track">
                        {trackWidth > 0 && (
                            <svg width={trackWidth} height={AXIS_HEIGHT}>
                                {marks.map((minute, index) => (index % labelEvery === 0 ? (
                                    <text key={minute} className="ss-sched__tick" x={x(minute)} y={18} textAnchor="middle">
                                        {clock(minute)}
                                    </text>
                                ) : null))}
                            </svg>
                        )}
                    </div>
                    <span />
                </div>

                <div role="list" aria-label={intl.formatMessage({ id: 'ServicesStatisticsScheduleListAria' })}
                    aria-description={intl.formatMessage({ id: 'ServicesStatisticsScheduleDetailHint' })}>
                    {trackWidth > 0 && rows.map((row, index) => {
                        const label = rowText(row);
                        const roving = rows.length > 8;
                        const tabbable = !roving || row.key === activeKey || (activeKey === null && index === 0);
                        return (
                            <div
                                key={row.key}
                                role="listitem"
                                aria-label={label}
                                tabIndex={tabbable ? 0 : -1}
                                className={`ss-sched__row${compact ? ' ss-sched__row--compact' : ''}${row.key === activeKey ? ' ss-sched__row--active' : ''}`}
                                onMouseEnter={() => setActiveKey(row.key)}
                                onFocus={() => setActiveKey(row.key)}
                                onKeyDown={moveFocus}
                            >
                                <span className={compact ? 'ss-sched__label ss-sched__label--inline' : 'ss-sched__label'} aria-hidden="true">
                                    {compact ? (
                                        <>
                                            <span className="ss-sched__day">{shortDay(row.date ?? '')}</span>
                                            <span className="ss-sched__sub">{dayShort(row.dayOfWeek)}</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="ss-sched__day">{dayShort(row.dayOfWeek)}</span>
                                            {row.blockLabel && <span className="ss-sched__sub">{row.blockLabel}</span>}
                                        </>
                                    )}
                                </span>
                                <svg className="ss-sched__plot" width={trackWidth} height={rowHeight} aria-hidden="true">
                                    {marks.map(minute => (
                                        <line key={minute} className="ss-sched__grid" x1={x(minute)} x2={x(minute)} y1={0} y2={rowHeight} />
                                    ))}
                                    {renderBars(row)}
                                </svg>
                                <span className="ss-sched__end" aria-hidden="true">
                                    {row.late
                                        ? <>
                                            <span className="ss-sched__over-label">
                                                {intl.formatMessage({ id: 'ServicesStatisticsScheduleOverrunLabel' }, { minutes: formatInt(row.overrunMinutes) })}
                                            </span>
                                            {!compact && (
                                                <span className="ss-sched__ontime">
                                                    {intl.formatMessage({ id: 'ServicesStatisticsScheduleLateShare' },
                                                        { days: formatInt(row.overrunDays), total: row.days })}
                                                </span>
                                            )}
                                        </>
                                        : <span className="ss-sched__ontime">{intl.formatMessage({ id: 'ServicesStatisticsScheduleOnTimeLabel' })}</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
            )}

            <p className="ss-sched__detail" aria-hidden="true">
                {active ? rowText(active) : null}
            </p>
        </div>
    );
}
